// src/components/sourcex/sourcexTourSteps.js
//
// The SourceX built-documents tour (2026-10-29), in page order. Pure data, tested
// (sourcexTourSteps.test.js). Each step spotlights the element marked
// data-tour="<target>" in SectionEditor.js; when that element isn't on screen
// (no table in this visit, the Outline hidden on a narrow screen, ...), the card
// shows `fallback`, which says where to find it, so the tour never breaks.
//
// `needs`: what the page must show first (SectionEditor's prepareTourStep, view only,
// nothing saved). It looks through the WHOLE visit and opens the heading the item is
// under; only if the visit has none does the card show `fallback` (decided
// 2026-10-29). It never switches to another visit.
//   "visit"    a visit is selected
//   "expanded" + its first heading is open (heading buttons)
//   "question" + the heading holding its first ordinary question is open
//   "table"    + the heading holding its first table (log) question is open
//   "editor"   + that ordinary question's editor is open (preferring a write-in one,
//              so Pre-fill shows). Closed again, unsaved, by the next step that
//              doesn't need it, or when the tour ends.

// Choice types mark their pre-fill on a choice; a table edits it cell by cell and has
// no "Add another answer"; a signature has neither. So the editor steps prefer a
// write-in question, where every editor step has something to point at.
const NOT_WRITE_IN = new Set(["Header", "Table", "Select", "MultiSelect", "Signature"]);
const isTable = (q) => q && q.type === "Table" && (q.columns || []).length > 0;
const isOrdinary = (q) => q && q.type !== "Header" && !isTable(q);

/**
 * The question a step is about, in the WHOLE visit (not just what's on screen), and
 * the heading it's under, so the page can open that heading. PURE.
 *
 * @param {object[]} questions one visit's questions
 * @param {"question"|"table"|"editor"} kind
 * @returns {{index: number, headingIndex: number} | null} headingIndex -1 = above
 *   the first heading (those are never closed); null = the visit has none
 */
export const findTourItem = (questions, kind) => {
  const qs = Array.isArray(questions) ? questions : [];
  let index = -1;
  if (kind === "table") index = qs.findIndex(isTable);
  else if (kind === "editor") {
    index = qs.findIndex((q) => isOrdinary(q) && !NOT_WRITE_IN.has(q.type));
    if (index < 0) index = qs.findIndex((q) => isOrdinary(q) && q.type !== "Signature");
    if (index < 0) index = qs.findIndex(isOrdinary);
  } else if (kind === "question") index = qs.findIndex(isOrdinary);
  if (index < 0) return null;
  let headingIndex = -1;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (qs[i] && qs[i].type === "Header") {
      headingIndex = i;
      break;
    }
  }
  return { index, headingIndex };
};

export const TOUR_STEPS = [
  {
    id: "intro",
    target: "tour-button",
    title: "Welcome to built documents",
    body: "A quick look at what this page can do. Use Next and Back, or press Esc to stop at any time.",
  },
  {
    id: "section-picker",
    target: "section-picker",
    title: "Sections",
    body: "Switch between the visit schedule, standing logs and the I/E checklist.",
  },
  {
    id: "add-section",
    target: "add-section",
    title: "Add Section and standing logs",
    body: "Add a blank section, or a ready-made log (Concomitant Medication, Medical/Surgical History). A log becomes its own section in the Section list, not part of a visit, and prints on its own landscape page.",
  },
  {
    id: "section-manage",
    target: "section-manage",
    title: "Move or delete a section",
    body: "Change the order of sections, or delete one.",
    fallback: "With more than one section, arrows and a delete button appear next to Add Section.",
  },
  {
    id: "visit-picker",
    target: "visit-picker",
    title: "Choose a visit",
    body: "Pick the visit to work on. Drag visits in this list to change their order.",
    needs: "visit",
    fallback: "Pick a section first; its visits are listed next to the Section picker.",
  },
  {
    id: "visit-manage",
    target: "visit-manage",
    title: "Add, move or delete visits",
    body: "Add a new visit, move the selected one earlier or later, or delete it.",
    needs: "visit",
    fallback: "These buttons sit next to the visit picker. Logs and the I/E checklist have only one visit.",
  },
  {
    id: "rename-visit",
    target: "rename-visit",
    title: "Rename a visit",
    body: "The pencil next to the visit's name renames it.",
    needs: "visit",
    fallback: "The pencil sits next to the visit's name at the top of the document. Logs can't be renamed here.",
  },
  {
    id: "paper-final",
    target: "paper-final",
    title: "Finalized on paper",
    body: "Tick this if this visit's source was completed on paper before SourceX. Nothing changes; exports just warn first.",
    needs: "visit",
    fallback: "The \"Finalized on paper\" tick is under the visit's name.",
  },
  {
    id: "undo",
    target: "undo",
    title: "Undo",
    body: "Undoes your last change, up to 25 steps back. It only works until you refresh or leave the page.",
    needs: "visit",
    fallback: "Undo is in the Tools box on the left, once a visit is open.",
  },
  {
    id: "use-template",
    target: "use-template",
    title: "Use from template",
    body: "Insert a standard question block (e.g. Physical Exam). Click it to add it at the top, or drag it onto the Outline or onto a question you're editing.",
    needs: "visit",
    fallback: "\"Use from template\" is in the Tools box on the left, once a visit is open.",
  },
  {
    id: "add-heading",
    target: "add-heading",
    title: "Add Heading",
    body: "Add a new heading (a section of questions) to the top of this visit.",
    needs: "visit",
    fallback: "\"Add Heading\" is in the Tools box on the left, once a visit is open.",
  },
  {
    id: "answer-types",
    target: "answer-types",
    title: "Answer types",
    body: "Drag an answer type (text, date, dropdown, table...) into a question you're editing.",
  },
  {
    id: "heading-toggle",
    target: "heading-toggle",
    title: "Open or close a heading",
    body: "Click a heading to show or hide the questions under it.",
    needs: "expanded",
    fallback: "This visit has no headings yet. Add one with \"Add Heading\".",
  },
  {
    id: "heading-add-question",
    target: "heading-add-question",
    title: "Add a question to a heading",
    body: "Adds a new question at the end of this heading.",
    needs: "expanded",
    fallback: "\"Add question\" is on every heading row.",
  },
  {
    id: "heading-rename",
    target: "heading-rename",
    title: "Rename a heading",
    body: "The pencil on a heading renames it.",
    needs: "expanded",
    fallback: "The pencil is on every heading row.",
  },
  {
    id: "heading-merge",
    target: "heading-merge",
    title: "Merge into another heading",
    body: "Moves all of this heading's questions into another heading of this visit, then removes this heading.",
    needs: "expanded",
    fallback: "The merge button is on every heading row; it needs at least two headings in the visit.",
  },
  {
    id: "heading-copy",
    target: "heading-copy",
    title: "Copy a heading to other visits",
    body: "Copies this heading and every question under it into the other visits you pick.",
    needs: "expanded",
    fallback: "The copy button is on every heading row.",
  },
  {
    id: "question-insert",
    target: "question-insert",
    title: "Add a question right below",
    body: "The + on a question adds a new question directly under it.",
    needs: "question",
    fallback: "This visit has no questions yet. Once it does, every question has a + to add one right below it.",
  },
  {
    id: "question-edit",
    target: "question-edit",
    title: "Edit a question",
    body: "The pencil opens a question's editor. The next few steps show what's inside it.",
    needs: "question",
    fallback: "This visit has no questions yet. Once it does, every question has a pencil to edit it.",
  },
  {
    id: "builder-type",
    target: "builder-type",
    title: "Answer format",
    body: "Choose how the question is answered: text, number, date, dropdown, checkboxes, a table and more.",
    needs: "editor",
    fallback: "Open a question with its pencil: the answer format is the first box under the question.",
  },
  {
    id: "builder-add-answer",
    target: "builder-add-answer",
    title: "Add another answer",
    body: "Adds a second answer field to this same question, for example a value and its unit, printed together.",
    needs: "editor",
    fallback: "Open a question with its pencil: \"Add another answer\" sits beside Required.",
  },
  {
    id: "builder-prefill",
    target: "builder-prefill",
    title: "Pre-fill a value",
    body: "Type a value here and it prints already filled in on the blank form. For a dropdown, click a choice to print it already marked.",
    needs: "editor",
    fallback: "Open a question with its pencil: each answer has a blue \"Pre-fill value\" spot (not on signatures).",
  },
  {
    id: "builder-conditional",
    target: "builder-conditional",
    title: "Make conditional",
    body: "Only show this question after a certain answer, e.g. \"If No, why?\" appears only when the answer above is No.",
    needs: "editor",
    fallback: "Open a question with its pencil: \"Make conditional\" sits beside Required.",
  },
  {
    id: "builder-required",
    target: "builder-required",
    title: "Required",
    body: "Tick this to mark the question as one that must be answered. It shows a red * on the form.",
    needs: "editor",
    fallback: "Open a question with its pencil: the Required tick is next to the question's text.",
  },
  {
    id: "question-copy",
    target: "question-copy",
    title: "Copy one question",
    body: "Copies just this question, into another heading of this visit or into another visit.",
    needs: "question",
    fallback: "This visit has no questions yet. Once it does, every question has a copy button.",
  },
  {
    id: "split-table",
    target: "split-table",
    title: "Split a table",
    body: "Not really a log? Splits a table into one question per column.",
    needs: "table",
    fallback: "This visit has no table (log) questions. When a question is a table, a split button sits next to its pencil.",
  },
  {
    id: "outline",
    target: "outline",
    title: "Outline",
    body: "Click a line to jump to it. Drag headings or questions here to move them.",
    needs: "visit",
    fallback: "On a wider screen, the Outline shows on the right of the document.",
  },
  {
    id: "visit-notes",
    target: "visit-notes",
    title: "Visit notes",
    body: "Notes for this visit, at the bottom of the document.",
    needs: "visit",
    fallback: "The Notes box is at the bottom of each visit.",
  },
  {
    id: "preview",
    target: "preview",
    title: "Preview",
    body: "See this visit exactly as it will print.",
  },
  {
    id: "version-history",
    target: "version-history",
    title: "Version History",
    body: "Earlier approved versions of this visit: compare them, export one, or restore one.",
  },
  {
    id: "audit-trail",
    target: "audit-trail",
    title: "Audit Trail",
    body: "Every change made here, who made it and when.",
  },
  {
    id: "export",
    target: "export",
    title: "Export / Print",
    body: "Download a visit as a Word file, or print or save it as a PDF.",
  },
  {
    id: "approve",
    target: "approve",
    title: "Approve",
    body: "Approves this visit and saves it as a version. After a change, it reads Re-approve.",
    needs: "visit",
    fallback: "The approve button is in the banner at the top of the page.",
  },
  {
    id: "jump",
    target: "jump",
    title: "Jump to top or bottom",
    body: "These buttons jump to the top or the bottom of the visit.",
    needs: "visit",
    fallback: "Once a visit is open, jump buttons sit at the bottom right of the screen.",
  },
  {
    id: "outro",
    target: "tour-button",
    title: "Take the tour again any time",
    body: "The Tour button stays here, at the top of the page. Click it whenever you want this tour again.",
  },
];
